import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Sparkles, Send, Settings as SettingsIcon, RotateCcw, Bot, User as UserIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHART_COLORS = ['#0f766e', '#d97706', '#dc2626', '#2563eb', '#9333ea', '#16a34a', '#db2777', '#0891b2'];

const formatINRAxis = (v) => {
  const n = Number(v);
  if (Math.abs(n) >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (Math.abs(n) >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (Math.abs(n) >= 1000) return `₹${(n / 1000).toFixed(0)}k`;
  return `₹${n}`;
};
const formatINRTooltip = (v) =>
  `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const ChartBlock = ({ spec }) => {
  const { type, data, x_key, y_keys, title } = spec;
  if (!Array.isArray(data) || data.length === 0) return null;

  const renderContent = () => {
    if (type === 'pie') {
      return (
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={100}
            label={(entry) => entry.name}
          >
            {data.map((entry, idx) => (
              <Cell key={entry.name || `cell-${idx}`} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(v) => formatINRTooltip(v)} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
        </PieChart>
      );
    }
    const keys = y_keys && y_keys.length ? y_keys : Object.keys(data[0]).filter((k) => k !== x_key);
    const commonProps = { data, margin: { top: 5, right: 10, left: 10, bottom: 5 } };
    if (type === 'line') {
      return (
        <LineChart {...commonProps}>
          <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
          <XAxis dataKey={x_key} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={formatINRAxis} />
          <Tooltip formatter={(v) => formatINRTooltip(v)} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {keys.map((k, idx) => (
            <Line
              key={k}
              type="monotone"
              dataKey={k}
              stroke={CHART_COLORS[idx % CHART_COLORS.length]}
              strokeWidth={2}
              dot={false}
            />
          ))}
        </LineChart>
      );
    }
    if (type === 'area') {
      return (
        <AreaChart {...commonProps}>
          <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
          <XAxis dataKey={x_key} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={formatINRAxis} />
          <Tooltip formatter={(v) => formatINRTooltip(v)} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {keys.map((k, idx) => (
            <Area
              key={k}
              type="monotone"
              dataKey={k}
              stroke={CHART_COLORS[idx % CHART_COLORS.length]}
              fill={CHART_COLORS[idx % CHART_COLORS.length]}
              fillOpacity={0.3}
            />
          ))}
        </AreaChart>
      );
    }
    // bar
    return (
      <BarChart {...commonProps}>
        <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
        <XAxis dataKey={x_key} tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} tickFormatter={formatINRAxis} />
        <Tooltip formatter={(v) => formatINRTooltip(v)} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {keys.map((k, idx) => (
          <Bar key={k} dataKey={k} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
        ))}
      </BarChart>
    );
  };

  return (
    <div className="mt-3 border rounded-md p-3 bg-background" data-testid="ai-chart-block">
      {title && <div className="text-sm font-semibold mb-2">{title}</div>}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          {renderContent()}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const mdComponents = {
  p: ({ node, ...props }) => <p className="mb-2 last:mb-0 leading-relaxed" {...props} />,
  h1: ({ node, ...props }) => (
    <h1 className="text-lg font-heading font-semibold mt-3 mb-2 text-foreground" {...props} />
  ),
  h2: ({ node, ...props }) => (
    <h2 className="text-base font-heading font-semibold mt-3 mb-2 text-foreground" {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="text-sm font-heading font-semibold mt-2 mb-1 text-foreground" {...props} />
  ),
  ul: ({ node, ...props }) => (
    <ul className="list-disc pl-5 mb-2 space-y-1" {...props} />
  ),
  ol: ({ node, ...props }) => (
    <ol className="list-decimal pl-5 mb-2 space-y-1" {...props} />
  ),
  li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
  strong: ({ node, ...props }) => (
    <strong className="font-semibold text-foreground" {...props} />
  ),
  em: ({ node, ...props }) => <em className="italic" {...props} />,
  a: ({ node, ...props }) => (
    <a
      className="text-primary underline underline-offset-2 hover:opacity-80"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    />
  ),
  code: ({ node, inline, className, children, ...props }) =>
    inline ? (
      <code className="bg-muted px-1 py-0.5 rounded text-[0.85em] font-mono" {...props}>
        {children}
      </code>
    ) : (
      <pre className="bg-muted border rounded-md p-3 overflow-x-auto text-xs font-mono mb-2">
        <code {...props}>{children}</code>
      </pre>
    ),
  blockquote: ({ node, ...props }) => (
    <blockquote
      className="border-l-4 border-accent/50 pl-3 italic text-muted-foreground mb-2"
      {...props}
    />
  ),
  hr: () => <hr className="my-3 border-border" />,
  table: ({ node, ...props }) => (
    <div className="my-3 overflow-x-auto rounded-md border border-border">
      <table className="w-full border-collapse text-xs" {...props} />
    </div>
  ),
  thead: ({ node, ...props }) => (
    <thead className="bg-muted/70 text-foreground" {...props} />
  ),
  tbody: ({ node, ...props }) => <tbody className="divide-y divide-border" {...props} />,
  tr: ({ node, ...props }) => <tr className="hover:bg-muted/30" {...props} />,
  th: ({ node, ...props }) => (
    <th
      className="px-3 py-2 text-left font-semibold border-b border-border whitespace-nowrap"
      {...props}
    />
  ),
  td: ({ node, ...props }) => (
    <td className="px-3 py-2 align-top whitespace-nowrap" {...props} />
  ),
};

const Message = ({ msg }) => {
  const isUser = msg.role === 'user';
  return (
    <div
      className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}
      data-testid={`ai-message-${msg.role}`}
    >
      <div
        className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${
          isUser ? 'bg-primary/10' : 'bg-accent/10'
        }`}
      >
        {isUser ? <UserIcon className="h-4 w-4 text-primary" /> : <Bot className="h-4 w-4 text-accent" />}
      </div>
      <div className={`flex-1 space-y-2 ${isUser ? 'text-right' : ''}`} style={{ maxWidth: '85%' }}>
        <div
          className={`inline-block px-4 py-2.5 rounded-lg text-sm ${
            isUser
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-foreground border'
          }`}
        >
          {isUser ? (
            <div className="whitespace-pre-wrap">{msg.content}</div>
          ) : (
            <div className="text-sm max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                {msg.content || ''}
              </ReactMarkdown>
            </div>
          )}
        </div>
        {!isUser && msg.charts?.map((c, i) => <ChartBlock key={i} spec={c} />)}
      </div>
    </div>
  );
};

const EXAMPLE_PROMPTS = [
  "What's our liquidity position today?",
  'Plot the total liquidity trend for the last 30 days.',
  'Which budget heads are over 80% utilized?',
  'List invoices due for payment in MPL 2026.',
  'Show me revenue status by team as a bar chart.',
  'Who are the top 5 vendors by PO value?',
];

const AIAssistant = () => {
  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [model, setModel] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ai/config`, { withCredentials: true });
      setConfig(res.data);
      setModel(res.data.default_model);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load AI config');
    } finally {
      setLoadingConfig(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  const sendMessage = async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || sending) return;
    const nextMessages = [...messages, { role: 'user', content: trimmed }];
    setMessages(nextMessages);
    setInput('');
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/ai/chat`,
        {
          messages: nextMessages.map((m) => ({ role: m.role, content: m.content })),
          model,
        },
        { withCredentials: true }
      );
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content: res.data.content || '(No response)',
          charts: res.data.charts || [],
        },
      ]);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Request failed';
      setMessages((m) => [...m, { role: 'assistant', content: `**Error:** ${detail}`, charts: [] }]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (loadingConfig) {
    return (
      <div className="py-12 text-center text-muted-foreground">Loading AI Assistant…</div>
    );
  }

  if (!config?.configured) {
    return (
      <div data-testid="ai-empty-state" className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-accent" /> AI Assistant
            </CardTitle>
            <CardDescription>
              The AI Assistant needs an OpenRouter API key before it can answer questions about your data.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Get an API key from{' '}
              <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer" className="underline">
                openrouter.ai/keys
              </a>{' '}
              and paste it in the Settings page under AI Assistant.
            </p>
            <Button asChild data-testid="configure-ai-button">
              <Link to="/settings">
                <SettingsIcon className="h-4 w-4 mr-2" /> Configure in Settings
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="ai-assistant-page" className="h-[calc(100vh-10rem)] flex flex-col max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-accent" /> AI Assistant
          </h1>
          <p className="text-sm text-muted-foreground">Your AI-powered finance analyst for MPL — ask any question across tournaments, budgets, liquidity or revenue.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger className="w-56" data-testid="ai-model-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(config?.supported_models || []).map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMessages([])}
            disabled={messages.length === 0}
            data-testid="ai-reset-button"
          >
            <RotateCcw className="h-4 w-4 mr-1" /> New
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto space-y-4 pr-2"
        data-testid="ai-messages"
      >
        {messages.length === 0 && (
          <div className="space-y-3">
            <Card>
              <CardContent className="py-6 text-center space-y-3">
                <Sparkles className="h-10 w-10 text-accent mx-auto" />
                <div className="text-sm text-muted-foreground">
                  Try one of these to get started:
                </div>
                <div className="flex flex-wrap justify-center gap-2 mt-3">
                  {EXAMPLE_PROMPTS.map((p, i) => (
                    <Badge
                      key={p}
                      variant="outline"
                      className="cursor-pointer hover:bg-accent/10 px-3 py-1.5 text-xs"
                      onClick={() => sendMessage(p)}
                      data-testid={`ai-example-${i}`}
                    >
                      {p}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        {messages.map((m, i) => (
          <Message key={m.id || `msg-${i}`} msg={m} />
        ))}
        {sending && (
          <div className="flex gap-3">
            <div className="flex-shrink-0 h-8 w-8 rounded-full bg-accent/10 flex items-center justify-center">
              <Bot className="h-4 w-4 text-accent" />
            </div>
            <div className="px-4 py-2.5 rounded-lg bg-muted border text-sm">
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" style={{ animationDelay: '300ms' }} />
                <span className="ml-2 text-xs text-muted-foreground">Thinking…</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="mt-4 border rounded-lg bg-background p-2 flex items-end gap-2 shadow-sm">
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about liquidity, budgets, POs, invoices, revenue…"
          rows={2}
          className="flex-1 resize-none border-0 focus-visible:ring-0 text-sm"
          data-testid="ai-input"
          disabled={sending}
        />
        <Button
          onClick={() => sendMessage()}
          disabled={sending || !input.trim()}
          data-testid="ai-send-button"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default AIAssistant;
